import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FireBonus, FireBonusTop } from '@/lib/actions';

const COLS = 6;
const ROWS = 4;

function topLabel(t: FireBonusTop): string {
  if (t.type === 'mult') return `x${t.value}`;
  return t.tier === 'mini' ? 'MINI' : t.tier === 'minor' ? 'MINOR' : t.tier === 'major' ? 'MAJOR' : 'GRAND';
}
function topColor(t: FireBonusTop): string {
  if (t.type === 'mult') return 'from-amber-500 to-orange-600';
  return t.tier === 'mini' ? 'from-sky-500 to-blue-600'
    : t.tier === 'minor' ? 'from-emerald-500 to-green-600'
    : t.tier === 'major' ? 'from-fuchsia-500 to-purple-600'
    : 'from-yellow-300 via-amber-500 to-red-600';
}

// Audio procedural propio del bonus
function useBonusAudio() {
  const ctxRef = useRef<AudioContext | null>(null);
  const getCtx = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  };
  const tone = (freq: number, start: number, dur: number, type: OscillatorType = 'sine', gain = 0.18) => {
    const ctx = getCtx(); const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = type; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
    const t = ctx.currentTime + start;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(gain, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.start(t); o.stop(t + dur + 0.02);
  };
  return {
    orb: (value: number) => { const f = 420 + Math.min(value, 50) * 22; tone(f, 0, 0.12, 'triangle', 0.16); tone(f * 2, 0.01, 0.08, 'sine', 0.08); },
    fill: () => { [523, 659, 784].forEach((f, i) => tone(f, i * 0.05, 0.25, 'triangle', 0.2)); },
    jackpot: () => { [659, 880, 1047, 1319].forEach((f, i) => tone(f, i * 0.08, 0.4, 'triangle', 0.25)); },
    final: () => { [392, 523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.1, 0.5, 'triangle', 0.26)); tone(1047, 0.55, 0.7, 'sine', 0.2); },
  };
}

function computeTotal(grid: (number | null)[][], tops: FireBonusTop[]): number {
  let total = 0;
  for (let c = 0; c < COLS; c++) {
    const sum = grid[c].reduce((s, v) => s + (v || 0), 0);
    const filled = grid[c].every((v) => v !== null);
    let contribution = sum;
    if (filled) contribution = tops[c].type === 'mult' ? sum * tops[c].value : sum + tops[c].value;
    total += contribution;
  }
  return total;
}

export function FireBonusOverlay({ bonus, betAmount, winningNumber, payout, onComplete }: {
  bonus: FireBonus; betAmount: number; winningNumber: number; payout: number; onComplete: () => void;
}) {
  const audio = useBonusAudio();
  const [grid, setGrid] = useState<(number | null)[][]>(() => Array.from({ length: COLS }, () => Array.from({ length: ROWS }, () => null)));
  const [spins, setSpins] = useState(3);
  const [filledCols, setFilledCols] = useState<Set<number>>(new Set());
  const [justDropped, setJustDropped] = useState<string>('');
  const [phase, setPhase] = useState<'intro' | 'playing' | 'done'>('intro');
  const [shake, setShake] = useState(0);
  const [respinPulse, setRespinPulse] = useState(false);
  const [runningTotal, setRunningTotal] = useState(0);
  const [displayMult, setDisplayMult] = useState(0);
  const gridRef = useRef<(number | null)[][]>(grid.map((c) => [...c]));

  useEffect(() => {
    let cancelled = false;
    const timers: any[] = [];
    timers.push(setTimeout(() => { if (!cancelled) setPhase('playing'); }, 1500));

    let i = 0;
    const stepEvery = 950;

    const runStep = () => {
      if (cancelled) return;
      if (i >= bonus.events.length) {
        timers.push(setTimeout(() => {
          if (cancelled) return;
          setPhase('done'); audio.final();
          // Cuenta el multiplicador final
          const target = bonus.finalMultiplier; const t0 = Date.now(); const dur = 1400;
          const tick = () => {
            const p = Math.min(1, (Date.now() - t0) / dur);
            setDisplayMult(Math.round(target * (1 - Math.pow(1 - p, 3))));
            if (p < 1 && !cancelled) requestAnimationFrame(tick);
          };
          tick();
        }, 700));
        return;
      }
      const ev = bonus.events[i];
      const prevSpins = spinsRef.current;
      ev.orbs.forEach((o, k) => { gridRef.current[o.col][o.row] = o.value; timers.push(setTimeout(() => audio.orb(o.value), k * 90)); });
      setGrid(gridRef.current.map((c) => [...c]));
      setSpins(ev.spinsRemaining);
      spinsRef.current = ev.spinsRemaining;
      setRunningTotal(computeTotal(gridRef.current, bonus.tops));
      if (ev.orbs.length > 0) {
        setJustDropped(ev.orbs.map((o) => `${o.col}-${o.row}`).join(','));
        setShake((s) => s + 1);
        if (ev.spinsRemaining >= 3 && prevSpins < 3) { setRespinPulse(true); setTimeout(() => setRespinPulse(false), 500); }
      }
      if (ev.filledCols && ev.filledCols.length > 0) {
        setFilledCols((prev) => { const n = new Set(prev); ev.filledCols.forEach((c) => n.add(c)); return n; });
        const hasJackpot = ev.filledCols.some((c) => bonus.tops[c].type === 'jackpot');
        if (hasJackpot) audio.jackpot(); else audio.fill();
        setShake((s) => s + 2);
      }
      i++;
      timers.push(setTimeout(runStep, stepEvery));
    };
    timers.push(setTimeout(runStep, 1600));

    return () => { cancelled = true; timers.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spinsRef = useRef(3);

  return (
    <motion.div
      animate={{ x: shake ? [0, -6, 6, -4, 4, 0] : 0 }} transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[95] flex items-center justify-center overflow-hidden bg-black/95 p-4">
      {/* Fondo de fuego */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(249,115,22,0.5),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(220,38,38,0.35),transparent_50%)]" />
      {Array.from({ length: 36 }).map((_, i) => (
        <motion.span key={i} className="pointer-events-none absolute text-2xl"
          initial={{ y: '100vh', x: `${(i * 31) % 100}vw`, opacity: 0 }}
          animate={{ y: '-12vh', opacity: [0, 0.95, 0] }}
          transition={{ duration: 2.6 + (i % 5), repeat: Infinity, delay: (i % 12) * 0.25, ease: 'easeIn' }}>
          {i % 3 === 0 ? '🔥' : i % 3 === 1 ? '✨' : '🔥'}
        </motion.span>
      ))}

      <div className="relative w-full max-w-2xl">
        {/* Intro */}
        <AnimatePresence>
          {phase === 'intro' && (
            <motion.div initial={{ scale: 0, rotate: -15 }} animate={{ scale: [0, 1.3, 1], rotate: 0 }} exit={{ scale: 0, opacity: 0 }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center">
              <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.8, repeat: Infinity }}
                className="text-center font-headline text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-orange-400 to-red-600 drop-shadow-[0_0_30px_rgba(249,115,22,0.9)]">
                <Flame className="mx-auto mb-2 h-16 w-16 text-orange-400" />
                MEGA FIRE BLAZE!
              </motion.div>
              <p className="mt-2 text-orange-200/80">¡Número {winningNumber} en fuego!</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={cn('transition-opacity duration-500', phase === 'intro' ? 'opacity-20' : 'opacity-100')}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-headline text-2xl font-black text-orange-300"><Flame className="h-6 w-6" /> Hold &amp; Win</h3>
            <motion.div animate={respinPulse ? { scale: [1, 1.35, 1] } : {}} transition={{ duration: 0.4 }}
              className={cn('rounded-xl border-2 px-4 py-1.5 text-center transition-colors', respinPulse ? 'border-green-400 bg-green-500/20' : 'border-orange-400/50 bg-black/60')}>
              <p className="text-[0.6rem] uppercase tracking-widest text-orange-200/60">Giros</p>
              <p className="text-2xl font-black text-orange-300">{spins}</p>
            </motion.div>
          </div>

          {/* Topes de columna */}
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
            {bonus.tops.map((t, c) => (
              <motion.div key={c} animate={filledCols.has(c) ? { scale: [1, 1.2, 1.05] } : {}} transition={{ duration: 0.5 }}
                className={cn('rounded-lg bg-gradient-to-b p-1.5 text-center text-xs font-black text-white shadow transition-all', topColor(t),
                  filledCols.has(c) && 'ring-2 ring-white shadow-[0_0_22px_rgba(255,255,255,0.8)]')}>
                {topLabel(t)}
                {t.type === 'jackpot' && <div className="text-[0.55rem] font-bold opacity-80">{t.value}x</div>}
              </motion.div>
            ))}
          </div>

          {/* Cuadrícula */}
          <div className="mt-2 grid gap-2 rounded-2xl border-2 border-orange-500/30 bg-black/50 p-3 shadow-[inset_0_0_40px_rgba(249,115,22,0.15)]" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
            {Array.from({ length: ROWS }).map((_, row) => (
              Array.from({ length: COLS }).map((_, col) => {
                const v = grid[col][row];
                const isNew = justDropped.split(',').includes(`${col}-${row}`);
                return (
                  <div key={`${col}-${row}`} className={cn('relative flex aspect-square items-center justify-center rounded-lg border', v !== null ? 'border-orange-400/60' : 'border-white/8 bg-white/5')}>
                    {v !== null && (
                      <>
                        {isNew && (
                          <motion.span className="pointer-events-none absolute inset-0 rounded-lg bg-orange-400"
                            initial={{ opacity: 0.9, scale: 0.4 }} animate={{ opacity: 0, scale: 1.6 }} transition={{ duration: 0.5 }} />
                        )}
                        <motion.div
                          initial={isNew ? { scale: 0.2, y: -120, rotate: -220, opacity: 0 } : false}
                          animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
                          transition={{ type: 'spring', stiffness: 380, damping: 16 }}
                          className="relative grid h-[88%] w-[88%] place-items-center rounded-full bg-gradient-to-b from-yellow-300 via-orange-500 to-red-600 text-sm font-black text-white shadow-[0_0_16px_rgba(249,115,22,0.9)]">
                          <span className="absolute -top-1 text-xs">🔥</span>
                          x{v}
                        </motion.div>
                      </>
                    )}
                  </div>
                );
              })
            ))}
          </div>

          {/* Total en vivo */}
          <div className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-black/50 py-2">
            <span className="text-xs uppercase tracking-widest text-orange-200/60">Total</span>
            <motion.span key={runningTotal} initial={{ scale: 1.3 }} animate={{ scale: 1 }} className="font-headline text-2xl font-black text-amber-300">x{Math.min(runningTotal, 2000).toLocaleString('es-ES')}</motion.span>
          </div>
        </div>

        {/* Resultado final */}
        <AnimatePresence>
          {phase === 'done' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 z-30 flex items-center justify-center">
              <motion.div initial={{ scale: 0.6, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ type: 'spring', stiffness: 240, damping: 16 }}
                className="relative w-full max-w-md rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-950/95 via-black to-red-950/95 p-8 text-center shadow-[0_0_70px_rgba(249,115,22,0.7)]">
                <motion.div animate={{ rotate: [0, -8, 8, 0] }} transition={{ duration: 0.6, repeat: Infinity }}>
                  <Flame className="mx-auto mb-2 h-14 w-14 text-orange-400" />
                </motion.div>
                <p className="text-sm uppercase tracking-widest text-orange-200/70">Multiplicador final</p>
                <p className="font-headline text-6xl font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 to-orange-500 drop-shadow">x{displayMult.toLocaleString('es-ES')}</p>
                <p className="mt-3 flex items-center justify-center gap-2 text-2xl font-black text-green-400"><Coins className="h-6 w-6" /> +{payout.toLocaleString('es-ES')} fichas</p>
                <p className="mt-1 text-xs text-muted-foreground">Apuesta {betAmount} × {bonus.finalMultiplier.toLocaleString('es-ES')}</p>
                <Button onClick={onComplete} className="mt-6 h-12 w-full bg-gradient-to-r from-amber-400 to-red-600 text-base font-black text-black hover:brightness-110">¡COBRAR!</Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
